import React, { useState, useEffect, useMemo } from 'react';
import { Search, Download, Upload, MoreVertical, RefreshCw, Phone, Trash2, Edit, Ban } from 'lucide-react';
import { leadAPI, campaignAPI } from '../services/api';
import LeadImportDialog from '../components/LeadImportDialog';
import { EditLeadDialog } from '../components/EditLeadDialog';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { PageHeader } from '../components/ui/page-header';
import { TabBar } from '../components/ui/tab-bar';
import { SubTabBar } from '../components/ui/sub-tab-bar';
import { DataCard } from '../components/ui/data-card';
import { MetricCard } from '../components/ui/metric-card';
import { StatusPill } from '../components/ui/status-pill';
import { SectionGrid } from '../components/ui/section-grid';
import { EmptyState } from '../components/ui/empty-state';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map backend lead status -> StatusPill tone + label.
const STATUS_TONE = {
  new:        { tone: 'info',    label: 'New' },
  contacted:  { tone: 'success', label: 'Contacted' },
  qualified:  { tone: 'gold',    label: 'Qualified' },
  converted:  { tone: 'success', label: 'Converted' },
  booked:     { tone: 'gold',    label: 'Booked' },
  cold:       { tone: 'neutral', label: 'Cold' },
  dnc:        { tone: 'danger',  label: 'DNC' },
  no_answer:  { tone: 'neutral', label: 'No answer' },
};

const OUTCOME_TONE = {
  'Appointment Booked':     { tone: 'gold',    label: 'Appointment booked' },
  'Callback Requested':     { tone: 'info',    label: 'Callback requested' },
  'Completed - No Action':  { tone: 'neutral', label: 'No action' },
  'No Answer':              { tone: 'neutral', label: 'No answer' },
  'DNC':                    { tone: 'danger',  label: 'DNC' },
};

// Mask all but the last 4 digits of a phone number.
function maskPhone(phone) {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
}

const Leads = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const filters = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [leadsData, campaignsData] = await Promise.all([
        leadAPI.getAll(filters),
        campaignAPI.getAll()
      ]);
      setLeads(leadsData);
      setCampaigns(campaignsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchLeads = () => fetchData();

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLeads();
  };

  const handleDeleteLead = async (leadId, leadName) => {
    if (!window.confirm(`Are you sure you want to delete ${leadName}?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete lead');

      toast.success(`${leadName} has been deleted`);
      fetchLeads();
    } catch (err) {
      console.error('Error deleting lead:', err);
      toast.error('Failed to delete lead');
    }
  };

  const handleUpdateStatus = async (leadId, newStatus, leadName) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update lead');

      toast.success(`${leadName} marked as ${newStatus}`);
      fetchLeads();
    } catch (err) {
      console.error('Error updating lead:', err);
      toast.error('Failed to update lead status');
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setShowEditDialog(true);
  };

  const handleEditSuccess = () => {
    fetchLeads();
  };

  const handleCallLead = async (lead) => {
    try {
      const campaignId = lead.campaign_id;
      if (!campaignId) {
        toast.error('Lead is not assigned to a campaign');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/call/${lead.id}`, {
        method: 'POST'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to initiate call');
      }

      toast.success(`Calling ${lead.first_name} ${lead.last_name}...`);
      fetchLeads();
    } catch (err) {
      console.error('Error initiating call:', err);
      toast.error(err.message || 'Failed to initiate call');
    }
  };

  const handleExportLeads = () => {
    const headers = ['First Name', 'Last Name', 'Phone', 'Email', 'Status', 'Outcome', 'Last Contacted'];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        lead.first_name || '',
        lead.last_name || '',
        lead.phone || '',
        lead.email || '',
        lead.status || '',
        lead.outcome || '',
        lead.last_contacted ? new Date(lead.last_contacted).toLocaleDateString() : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success('Leads exported successfully');
  };

  // KPI calculations from real data already loaded.
  const kpis = useMemo(() => {
    const total = leads.length;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const newCount = leads.filter((l) => {
      if (l.status === 'new') return true;
      const created = l.created_at ? new Date(l.created_at).getTime() : null;
      return created != null && created >= sevenDaysAgo;
    }).length;
    const qualified = leads.filter((l) => l.status === 'qualified' || l.status === 'booked').length;
    const converted = leads.filter((l) => l.status === 'converted' || l.outcome === 'Appointment Booked').length;
    return { total, newCount, qualified, converted };
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return leads.filter((lead) => {
      if (sourceFilter !== 'all' && lead.source !== sourceFilter) return false;
      if (!q) return true;
      const name = `${lead.first_name || ''} ${lead.last_name || ''}`.toLowerCase();
      const phone = String(lead.phone || '');
      const email = String(lead.email || '').toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q);
    });
  }, [leads, searchQuery, sourceFilter]);

  const campaignNameById = useMemo(() => {
    const m = {};
    (campaigns || []).forEach((c) => { m[c.id] = c.name || c.id; });
    return m;
  }, [campaigns]);

  const statusTabs = [
    { id: 'all',       label: 'All' },
    { id: 'new',       label: 'New' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'qualified', label: 'Qualified' },
    { id: 'converted', label: 'Converted' },
    { id: 'cold',      label: 'Cold' },
  ];

  const sourceTabs = [
    { id: 'all',       label: 'All sources' },
    { id: 'web_form',  label: 'Web form' },
    { id: 'csv_import',label: 'CSV import' },
    { id: 'walk_in',   label: 'Walk-in' },
  ];

  const headerActions = (
    <>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        className="btn-2050 btn-ghost"
        aria-label="Refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        Refresh
      </button>
      <button
        type="button"
        onClick={handleExportLeads}
        className="btn-2050 btn-secondary"
      >
        <Download className="w-3.5 h-3.5" />
        Export
      </button>
      <button
        type="button"
        onClick={() => setShowImportDialog(true)}
        className="btn-2050 btn-primary"
      >
        <Upload className="w-3.5 h-3.5" />
        Import
      </button>
    </>
  );

  const renderTableBody = () => {
    if (error) {
      return (
        <EmptyState
          title="Couldn't load leads"
          description="Unable to load right now."
        />
      );
    }
    if (filteredLeads.length === 0) {
      return (
        <EmptyState
          title="No leads found"
          description={
            statusFilter !== 'all' || sourceFilter !== 'all' || searchQuery
              ? 'Try adjusting your filters.'
              : 'Import a CSV or wait for new leads to flow in.'
          }
        />
      );
    }
    return (
      <div className="overflow-x-auto -mx-6">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40">
              <th className="text-left px-6 py-3 font-medium">Name</th>
              <th className="text-left px-6 py-3 font-medium">Phone</th>
              <th className="text-left px-6 py-3 font-medium">Campaign</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-left px-6 py-3 font-medium">Last call</th>
              <th className="text-left px-6 py-3 font-medium">Outcome</th>
              <th className="text-right px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
            {filteredLeads.map((lead) => {
              const status = STATUS_TONE[lead.status] || { tone: 'neutral', label: lead.status || '—' };
              const outcomeMap = lead.outcome ? OUTCOME_TONE[lead.outcome] : null;
              return (
                <tr
                  key={lead.id}
                  className="row-2050 hover:bg-ink-50/60 dark:hover:bg-ink-900/40"
                  data-testid={`lead-row-${lead.id}`}
                >
                  <td className="px-6 py-3">
                    <div className="font-medium text-ink-900 dark:text-ink-50">
                      {lead.first_name} {lead.last_name}
                    </div>
                    {lead.email && (
                      <div className="text-xs text-ink-500 mt-0.5">{lead.email}</div>
                    )}
                  </td>
                  <td className="px-6 py-3 nums text-ink-700 dark:text-ink-200">
                    {maskPhone(lead.phone)}
                  </td>
                  <td className="px-6 py-3 text-ink-500">
                    {lead.campaign_id ? (campaignNameById[lead.campaign_id] || lead.campaign_id) : '—'}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={status.tone} label={status.label} dot />
                  </td>
                  <td className="px-6 py-3 text-ink-500 nums">
                    {lead.last_contacted
                      ? new Date(lead.last_contacted).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-6 py-3">
                    {outcomeMap ? (
                      <StatusPill tone={outcomeMap.tone} label={outcomeMap.label} />
                    ) : lead.outcome ? (
                      <StatusPill tone="neutral" label={lead.outcome} />
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="p-2" data-testid={`lead-action-${lead.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleCallLead(lead)}
                          className="gap-2 cursor-pointer"
                        >
                          <Phone className="w-4 h-4" />
                          Call now
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleEditLead(lead)}
                          className="gap-2 cursor-pointer"
                          data-testid="edit-lead-button"
                        >
                          <Edit className="w-4 h-4" />
                          Edit lead
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleUpdateStatus(lead.id, 'contacted', `${lead.first_name} ${lead.last_name}`)}
                          className="gap-2 cursor-pointer"
                        >
                          <Edit className="w-4 h-4" />
                          Mark as contacted
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleUpdateStatus(lead.id, 'dnc', `${lead.first_name} ${lead.last_name}`)}
                          className="gap-2 cursor-pointer text-amber-600"
                        >
                          <Ban className="w-4 h-4" />
                          Mark as DNC
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteLead(lead.id, `${lead.first_name} ${lead.last_name}`)}
                          className="gap-2 cursor-pointer text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete lead
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Customers"
        title="Leads"
        description="Manage prospects across every campaign."
        actions={headerActions}
      />

      <SectionGrid cols={4}>
        <MetricCard
          label="Total"
          value={kpis.total}
          helperText="All leads"
        />
        <MetricCard
          label="New (last 7d)"
          value={kpis.newCount}
          helperText="Recently added"
        />
        <MetricCard
          label="Qualified"
          value={kpis.qualified}
          helperText="Ready to convert"
        />
        <MetricCard
          label="Converted"
          value={kpis.converted}
          helperText="Appointment booked"
        />
      </SectionGrid>

      <TabBar
        value={statusFilter}
        onChange={setStatusFilter}
        items={statusTabs}
      />

      <DataCard
        title="Leads"
        description={`${filteredLeads.length} of ${leads.length} shown`}
        actions={
          <div className="flex items-center gap-3">
            <SubTabBar
              value={sourceFilter}
              onChange={setSourceFilter}
              items={sourceTabs}
            />
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                type="text"
                placeholder="Search name, phone, email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 pr-3 rounded-md border border-ink-200/70 dark:border-ink-800/70 bg-white/40 dark:bg-ink-900/30 text-sm focus-2050 outline-none w-56"
                data-testid="leads-search-input"
              />
            </div>
          </div>
        }
        bodyClassName="p-0"
        loading={loading && leads.length === 0}
      >
        {renderTableBody()}
      </DataCard>

      <LeadImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={fetchLeads}
        campaigns={campaigns}
      />

      <EditLeadDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        lead={editingLead}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default Leads;
